import jsPDF from "jspdf";
import { getAuthHeaders } from "../../../tools/session/clientSession";
import { getCompanyLogoWithAuth, resolveCityDataKey } from "../services/companyFileReader";
import { getReportFileName, getCitiesToPrint, getIndiceSintetico } from "./reportShared";
import { getCoverBackground, getDividerBackground, loadReportSlideImages } from "./reportImages";
import { getLineeCategoryForCity, getLineeScore } from "./ambito";
import { SCORE_SCALE_BAD, SCORE_SCALE_GOOD, SCORE_SCALE_MID, scoreScaleColor } from "../../../constants/scoreScale";

async function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

function detectImageFormat(dataUrl) {
  if (!dataUrl || typeof dataUrl !== "string") return null;
  if (dataUrl.startsWith("data:image/png")) return "PNG";
  if (dataUrl.startsWith("data:image/jpeg") || dataUrl.startsWith("data:image/jpg")) return "JPEG";
  return null;
}

/** Embed image only when data URL is a supported raster format (avoids jsPDF PNG signature errors). */
function addPdfImage(doc, dataUrl, x, y, w, h) {
  const format = detectImageFormat(dataUrl);
  if (!format) return false;
  try {
    doc.addImage(dataUrl, format, x, y, w, h);
    return true;
  } catch {
    return false;
  }
}

async function loadImageB64(url) {
  try {
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith("image/")) return null;
    const dataUrl = await blobToDataUrl(blob);
    return detectImageFormat(dataUrl) ? dataUrl : null;
  } catch { return null; }
}

async function loadBlobUrlAsDataUrl(blobUrl) {
  if (!blobUrl) return null;
  try {
    const res = await fetch(blobUrl);
    const blob = await res.blob();
    if (!blob.type.startsWith("image/")) return null;
    const dataUrl = await blobToDataUrl(blob);
    return detectImageFormat(dataUrl) ? dataUrl : null;
  } catch {
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
//  PALETTE
// ═══════════════════════════════════════════════════════════════════════════════
const C = {
  lime: "#A3E635",
  dark: "#353A0A",
  mid: "#4C5E12",
  yellow: "#FFF260",
  white: "#FFFFFF",
  text: "#1B1C1C",
  muted: "#4A4C44",
  track: "#EAEADC",
  border: "#E5E5CC",
  grid: "#E8E8E8",
  cream: "#F9F9F0",
  coverBg: "#1A2008",
  divBg: "#1F2A0A",
};
const SERIES = [C.dark, C.lime, C.yellow, "#7fa81e"];

// ═══════════════════════════════════════════════════════════════════════════════
//  LAYOUT A4 LANDSCAPE (mm)
// ═══════════════════════════════════════════════════════════════════════════════
const W = 297;
const H = 210;
const ML = 16;
const MR = 16;
const TBAR = 5;
const FOOT = 11;
const Y0 = TBAR + 9;
const YMAX = H - FOOT - 2;

// ═══════════════════════════════════════════════════════════════════════════════
//  HELPERS
// ═══════════════════════════════════════════════════════════════════════════════
function rgb(h) {
  return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
}
const SF = (d, h) => d.setFillColor(...rgb(h));
const SD = (d, h) => d.setDrawColor(...rgb(h));
const ST = (d, h) => d.setTextColor(...rgb(h));

function scoreGrad(v) {
  return scoreScaleColor(v, C.dark);
}
function scoreGradText(v) {
  return scoreScaleColor(v, C.dark);
}

function distCol(label, val) {
  const l = label.toLowerCase();
  if (/perfett|eccell|ottim|ben\s|pulito|assenz|nessun|complet|inequivoc|esaust|funzionant|^si$|presenti\se|rampa\se|percorsi\se/.test(l)) return SCORE_SCALE_GOOD;
  if (/comprom|trascur|scarsi|inadeg|insuff|non\svisib|non\sprot|assenza\sdi|illegg|sconfort|usurato|assenti\so|^no$/.test(l)) return SCORE_SCALE_BAD;
  if (/discret|parzial|suffic|abbastanz|adeg|moderat|solo\s/.test(l)) return SCORE_SCALE_MID;
  return scoreGrad(val * 10);
}

function onBar(bg) {
  const r = parseInt(bg.slice(1, 3), 16);
  const g = parseInt(bg.slice(3, 5), 16);
  const b = parseInt(bg.slice(5, 7), 16);
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  return lum > 130 ? C.dark : C.white;
}

const fmt = (n, dec = 1) => n.toFixed(dec).replace(".", ",");
const fmtP = (n) => `${Math.round(n * 100)}%`;

function capFirst(s) {
  return s.replace(/_/g, " ").replace(/^\w/, c => c.toUpperCase());
}

function getCittaObj(company) {
  return company?.citta || company?.["città"] || {};
}

function getCityData(company, city) {
  const citta = getCittaObj(company);
  const key = resolveCityDataKey(citta, city);
  return { key, data: citta[key] || {} };
}
// ═══════════════════════════════════════════════════════════════════════════════
//  RICONOSCIMENTO TIPO DATO
// ═══════════════════════════════════════════════════════════════════════════════
function isNumMap(o) {
  if (typeof o !== "object" || o === null) return false;
  return Object.values(o).every(v => v === null || typeof v === "number");
}
function isDist(data) {
  const vals = Object.values(data).filter(v => v !== null && v !== undefined && isFinite(v));
  if (!vals.length) return false;
  // If every value is between 0 and 1, treat as percentage distribution
  // (covers both proper distributions and per-item binary scores in sito_web/app).
  return vals.every(v => v >= 0 && v <= 1.001);
}
function isGrouped(o) {
  if (typeof o !== "object" || o === null) return false;
  const e = Object.entries(o);
  if (e.length < 2) return false;
  const ref = new Set(Object.keys(e[0][1] || {}));
  if (!ref.size) return false;
  return e.every(([, v]) =>
    typeof v === "object" && v !== null &&
    Object.values(v).every(iv => iv === null || typeof iv === "number") &&
    [...ref].every(k => k in v)
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  FRAME STANDARD (per pagine non-Lay)
// ═══════════════════════════════════════════════════════════════════════════════
function stdFrame(doc, company, city, pn, catLabel = "") {
  SF(doc, C.white); doc.rect(0, 0, W, H, "F");
  SF(doc, C.lime); doc.rect(0, 0, W, 2, "F");
  SF(doc, C.lime); doc.rect(0, H - 2, W, 2, "F");
  if (catLabel) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(5.5); ST(doc, C.mid);
    doc.text(catLabel.toUpperCase(), ML, 8);
  }
  doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); ST(doc, C.muted);
  doc.text(String(pn), W - MR, H - 4, { align: "right" });
}

// ═══════════════════════════════════════════════════════════════════════════════
//  MOTORE DI LAYOUT
// ═══════════════════════════════════════════════════════════════════════════════
class Lay {
  constructor(doc, company, city) {
    this.doc = doc;
    this.company = company;
    this.city = city;
    this.y = Y0;
    this.pn = 1;
    this.cat = "";
  }

  newPage() {
    this.doc.addPage();
    this.pn++;
    this._drawFrame();
    this.y = Y0;
  }

  _drawFrame() {
    const d = this.doc;
    SF(d, C.white); d.rect(0, 0, W, H, "F");
    SF(d, C.lime); d.rect(0, 0, W, 2, "F");
    SF(d, C.lime); d.rect(0, H - 2, W, 2, "F");
    if (this.cat) {
      d.setFont("helvetica", "bold"); d.setFontSize(5.5); ST(d, C.mid);
      d.text(this.cat.toUpperCase(), ML, 8);
    }
    d.setFont("helvetica", "normal"); d.setFontSize(6.5); ST(d, C.muted);
    d.text(String(this.pn), W - MR, H - 3.5, { align: "right" });
  }

  need(h) { if (this.y + h > YMAX) this.newPage(); }

  chartTitle(name) {
    const d = this.doc;
    d.setFont("helvetica", "bold"); d.setFontSize(8); ST(d, C.mid);
    d.text(capFirst(name), ML, this.y);
    SF(d, C.lime); d.rect(ML, this.y + 1.5, 30, 1.2, "F");
    this.y += 7;
  }

  hbar(label, val, maxV, color, lW, bW, rowH = 10, valStr) {
    const d = this.doc;
    const bX = ML + lW;
    const midY = this.y + rowH / 2;
    d.setFont("helvetica", "normal"); d.setFontSize(7); ST(d, C.text);
    d.text(label.replace(/_/g, " "), bX - 4, midY + 2, { align: "right", maxWidth: lW - 4 });
    SF(d, C.track); d.roundedRect(bX, this.y + 2.5, bW, rowH - 5, 1.5, 1.5, "F");
    if (val !== null && val !== undefined && isFinite(val) && val >= 0) {
      const fw = Math.max(2, (val / maxV) * bW);
      SF(d, color); d.roundedRect(bX, this.y + 2.5, fw, rowH - 5, 1.5, 1.5, "F");
      const vs = valStr ?? (maxV <= 1 ? fmtP(val) : fmt(val, maxV <= 10 ? 1 : 0));
      d.setFont("helvetica", "bold"); d.setFontSize(6.5);
      if (fw > 20) {
        ST(d, onBar(color));
        d.text(vs, bX + fw - 3, midY + 2, { align: "right" });
      } else {
        ST(d, C.dark);
        d.text(vs, bX + fw + 3, midY + 2);
      }
    }
    this.y += rowH;
  }

  drawDist(name, data) {
    const ents = Object.entries(data);
    if (!ents.length) return;
    const LW = 80; const BW = W - ML - MR - LW;
    const rowH = 11;
    this.need(ents.length * rowH + 16);
    this.chartTitle(name);
    ents.forEach(([k, v], i) => {
      const n = (v !== null && v !== undefined && isFinite(v)) ? v : null;
      this.need(rowH + 1);
      this.hbar(k, n, 1, n !== null ? distCol(k, n) : C.dark, LW, BW, rowH);
      if (i < ents.length - 1) {
        SD(this.doc, C.border); this.doc.setLineWidth(0.1);
        this.doc.line(ML + LW, this.y, W - MR, this.y);
      }
    });
    this.y += 6;
  }

  drawScore(name, data) {
    const ents = Object.entries(data);
    if (!ents.length) return;
    const vals = ents.map(([, v]) => v ?? 0).filter(isFinite);
    const maxV = Math.max(...vals, 10);
    const LW = 80; const BW = W - ML - MR - LW;
    const rowH = 11;
    this.need(ents.length * rowH + 16);
    this.chartTitle(name);
    ents.forEach(([k, v], i) => {
      const n = (v !== null && v !== undefined && isFinite(v)) ? v : null;
      this.need(rowH + 1);
      this.hbar(k, n, maxV, n !== null ? scoreGrad(n) : C.dark, LW, BW, rowH);
      if (i < ents.length - 1) {
        SD(this.doc, C.border); this.doc.setLineWidth(0.1);
        this.doc.line(ML + LW, this.y, W - MR, this.y);
      }
    });
    this.y += 6;
  }

  drawGrouped(name, data) {
    const cats = Object.keys(data);
    const sers = Object.keys(data[cats[0]] || {});
    if (!cats.length || !sers.length) return;
    const allV = cats.flatMap(c =>
      Object.values(data[c]).filter(v => v !== null && v !== undefined && isFinite(v))
    );
    const dist = allV.every(v => v >= 0 && v <= 1.001);
    const maxV = dist ? 1 : Math.max(...allV, 10);
    const LW = 80; const BW = W - ML - MR - LW;
    const SH = 8; const GAP = 5;
    const total = cats.length * (sers.length * SH + GAP) + 28;
    this.need(total);
    this.chartTitle(name);
    const d = this.doc;
    let lx = ML;
    sers.forEach((sk, i) => {
      SF(d, SERIES[i % SERIES.length]); d.roundedRect(lx, this.y - 4, 8, 4.5, 0.8, 0.8, "F");
      d.setFont("helvetica", "normal"); d.setFontSize(6.5); ST(d, C.muted);
      d.text(sk, lx + 10, this.y);
      lx += d.getTextWidth(sk) + 16;
    });
    this.y += 7;
    cats.forEach((cat, ci) => {
      this.need(sers.length * SH + GAP);
      d.setFont("helvetica", "bold"); d.setFontSize(7); ST(d, C.text);
      d.text(cat.replace(/_/g, " "), ML + LW - 4, this.y + SH / 2 + 2, { align: "right", maxWidth: LW - 4 });
      sers.forEach((sk, si) => {
        const v = data[cat]?.[sk] ?? null;
        const n = (v !== null && v !== undefined && isFinite(v)) ? v : null;
        const color = SERIES[si % SERIES.length];
        const bX = ML + LW;
        const midY = this.y + SH / 2;
        SF(d, C.track); d.roundedRect(bX, this.y + 1.5, BW, SH - 3, 1, 1, "F");
        if (n !== null) {
          const fw = Math.max(1.5, (n / maxV) * BW);
          SF(d, color); d.roundedRect(bX, this.y + 1.5, fw, SH - 3, 1, 1, "F");
          const vs = dist ? fmtP(n) : fmt(n, maxV <= 10 ? 1 : 0);
          d.setFont("helvetica", "bold"); d.setFontSize(5.5);
          if (fw > 14) {
            ST(d, onBar(color)); d.text(vs, bX + fw - 2, midY + 1.5, { align: "right" });
          } else {
            ST(d, C.dark); d.text(vs, bX + fw + 3, midY + 1.5);
          }
        }
        if (si > 0) {
          d.setFont("helvetica", "normal"); d.setFontSize(5.5); ST(d, C.muted);
          d.text(sk, ML + LW - 4, midY + 1.5, { align: "right" });
        }
        this.y += SH;
      });
      if (ci < cats.length - 1) {
        SD(d, C.border); d.setLineWidth(0.15);
        d.line(ML + LW, this.y + 1, W - MR, this.y + 1);
      }
      this.y += GAP;
    });
    this.y += 4;
  }

  render(name, data) {
    if (isGrouped(data)) {
      this.drawGrouped(name, data);
    } else if (isNumMap(data)) {
      isDist(data) ? this.drawDist(name, data) : this.drawScore(name, data);
    } else if (typeof data === "object" && data !== null) {
      for (const [k, v] of Object.entries(data)) {
        if (isNumMap(v) || isGrouped(v)) this.render(k, v);
      }
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
//  PAGINA COVER
// ═══════════════════════════════════════════════════════════════════════════════
function addCover(doc, company, city, logoB64, gametLogoB64, companyLogoB64, coverBgB64 = null) {
  if (!coverBgB64 || !addPdfImage(doc, coverBgB64, 0, 0, W, H)) {
    SF(doc, C.coverBg); doc.rect(0, 0, W, H, "F");
  }
  SF(doc, C.lime); doc.rect(0, 0, W, 2, "F");
  SF(doc, C.lime); doc.rect(0, H - 2, W, 2, "F");

  // ── GAMET logo — top-left, larger ──
  if (gametLogoB64) {
    const gametW = 60, gametH = 15;
    const gametX = ML - 5, gametY = 6;
    addPdfImage(doc, gametLogoB64, gametX, gametY, gametW, gametH);
  }

  // ── Exis logo — top-right (unchanged size) ──
  const logoW = 44, logoH = 44 / (2977 / 1080);
  const logoX = W - MR - logoW, logoY = 7;
  if (logoB64) {
    addPdfImage(doc, logoB64, logoX, logoY, logoW, logoH);
  } else {
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); ST(doc, C.lime);
    doc.text("Exis Mobility", logoX + 2, logoY + logoH / 2 + 2);
  }

  // ── Company logo — bottom-right, white, roughly same size as Exis ──
  if (companyLogoB64) {
    const clW = 44, clH = 44 / (2977 / 1080);
    const clX = W - MR - clW, clY = H - 4 - clH - 6;
    addPdfImage(doc, companyLogoB64, clX, clY, clW, clH);
  }

  doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); ST(doc, C.muted);
  doc.text("1", W - MR, H - 4, { align: "right" });

  const titleStr = `MYSTERY CLIENT ${company.anno}`;
  const titleLines = titleStr.split("\n");
  const maxLen = Math.max(...titleLines.map(l => l.length));
  const fontSize = maxLen > 14 ? 52 : maxLen > 10 ? 60 : 72;
  const lineSpacingMM = fontSize * 0.353 * 1.35;
  doc.setFont("helvetica", "bold"); doc.setFontSize(fontSize); ST(doc, C.white);
  let ty = H / 2 - ((titleLines.length - 1) * lineSpacingMM) / 2 - 8;
  let lastTy = ty;
  for (const tl of titleLines) {
    doc.text(tl, ML, ty);
    lastTy = ty;
    ty += lineSpacingMM;
  }

  SF(doc, C.lime); doc.rect(ML, lastTy + 8, 200, 4, "F");

  doc.setFont("helvetica", "normal"); doc.setFontSize(20); ST(doc, "#b8d07a");
  doc.text(city, ML, lastTy + 8 + 4 + 10);
}

// ═══════════════════════════════════════════════════════════════════════════════
//  PAGINA PREMESSA (slide 2 template)
// ═══════════════════════════════════════════════════════════════════════════════
function addPremessa(doc, company, city, pn) {
  stdFrame(doc, company, city, pn);
  doc.setFont("helvetica", "bold"); doc.setFontSize(22); ST(doc, C.dark);
  doc.text("Premessa", ML, Y0 + 10);
  SF(doc, C.lime); doc.rect(ML, Y0 + 13, W - ML - MR, 2.5, "F");

  const BLOCKS = [
    { text: "Il documento illustra l'indagine annuale di Mystery Client, promossa da ASSTRA, che analizza la qualità fornita dalle Aziende di Trasporto Pubblico Locale." },
    { text: "Il contenuto del documento è organizzato nelle seguenti sezioni:" },
    { bold: "Introduzione al Mystery Client:", text: " descrive la metodologia adottata per condurre l'indagine, elaborare e rappresentare i dati raccolti." },
    { bold: "Executive Summary:", text: " presenta in modo sintetico i risultati ottenuti per ciascuna area analizzata." },
    { bold: "Risultati dell'indagine (Elementi Specifici):", text: " dettaglia i risultati relativi agli aspetti specifici e distintivi del servizio, come il monitoraggio di punti vendita, fermate e linee." },
    { bold: "Risultati dell'indagine (Elementi Generali):", text: " approfondisce i risultati legati agli aspetti generali e non direttamente connessi al servizio di una singola linea, come il monitoraggio del call center, del sito web e delle applicazioni." },
  ];

  let y = Y0 + 26;
  const maxW = W - ML - MR;
  const FS = 11.5, LINE = 6.5, GAP = 11;
  for (const block of BLOCKS) {
    if (block.bold) {
      doc.setFont("helvetica", "bold"); doc.setFontSize(FS); ST(doc, C.dark);
      const bW = doc.getTextWidth(block.bold);
      doc.text(block.bold, ML + 6, y);
      doc.setFont("helvetica", "normal"); ST(doc, C.text);
      const wrapped = doc.splitTextToSize(block.text, maxW - 6 - bW);
      doc.text(wrapped[0], ML + 6 + bW, y);
      for (let i = 1; i < wrapped.length; i++) { y += LINE; doc.text(wrapped[i], ML + 6, y); }
    } else {
      doc.setFont("helvetica", "normal"); doc.setFontSize(FS); ST(doc, C.text);
      const wrapped = doc.splitTextToSize(block.text, maxW);
      doc.text(wrapped, ML, y);
      y += (wrapped.length - 1) * LINE;
    }
    y += GAP;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
//  PAGINE PESATURA + INDICATORI
// ═══════════════════════════════════════════════════════════════════════════════
function addPesaturaIndicatori(doc, company, city, pn) {
  const D = [
    // ── LINEE URBANE 40% ──
    { area: "LINEE URBANE", ap: "40%", cat: "OPERATIVITÀ", cp: "27%", param: "PUNTUALITÀ", pp: "60%" },
    { area: "", ap: "", cat: "", cp: "", param: "BIGLIETTO A BORDO", pp: "30%" },
    { area: "", ap: "", cat: "", cp: "", param: "FUNZ. OBLITERATRICE CARTACEA", pp: "3,3%" },
    { area: "", ap: "", cat: "", cp: "", param: "FUNZ. OBLITERATRICE ELETTRONICA", pp: "3,3%" },
    { area: "", ap: "", cat: "", cp: "", param: "STAMPIGLIATURA SUL BIGLIETTO", pp: "3,3%" },
    { area: "", ap: "", cat: "INFORMAZIONI", cp: "18%", param: "CORRETTA ESPOSIZIONE N. LINEA", pp: "38%" },
    { area: "", ap: "", cat: "", cp: "", param: "CORRETTA ESPOSIZIONE DESTINAZIONE", pp: "38%" },
    { area: "", ap: "", cat: "", cp: "", param: "SISTEMA DIFFUSIONE AUDIO", pp: "6%" },
    { area: "", ap: "", cat: "", cp: "", param: "REGOLAMENTO DI VIAGGIO", pp: "6%" },
    { area: "", ap: "", cat: "", cp: "", param: "PMV", pp: "12%" },
    { area: "", ap: "", cat: "COMFORT", cp: "29%", param: "RUMOROSITÀ INTERNA (MOTORE)", pp: "2%" },
    { area: "", ap: "", cat: "", cp: "", param: "RUMOROSITÀ FRENI", pp: "1%" },
    { area: "", ap: "", cat: "", cp: "", param: "VIBRAZIONI INTERNE VETTURA", pp: "4%" },
    { area: "", ap: "", cat: "", cp: "", param: "TEMPERATURA", pp: "28%" },
    { area: "", ap: "", cat: "", cp: "", param: "ODORI", pp: "4%" },
    { area: "", ap: "", cat: "", cp: "", param: "SPAZIO", pp: "38%" },
    { area: "", ap: "", cat: "", cp: "", param: "SEDILI", pp: "1%" },
    { area: "", ap: "", cat: "", cp: "", param: "ARREDO INTERNO", pp: "1%" },
    { area: "", ap: "", cat: "", cp: "", param: "FINESTRINI", pp: "1%" },
    { area: "", ap: "", cat: "", cp: "", param: "PORTE ANTERIORI", pp: "0,3%" },
    { area: "", ap: "", cat: "", cp: "", param: "PORTA CENTRALE", pp: "0,3%" },
    { area: "", ap: "", cat: "", cp: "", param: "PORTA POSTERIORE", pp: "0,3%" },
    { area: "", ap: "", cat: "", cp: "", param: "SEDILI IMBOTTITI", pp: "1%" },
    { area: "", ap: "", cat: "", cp: "", param: "PULIZIA PAVIMENTAZIONE", pp: "6%" },
    { area: "", ap: "", cat: "", cp: "", param: "PULIZIA VETRI", pp: "4%" },
    { area: "", ap: "", cat: "", cp: "", param: "PULIZIA SEDILI", pp: "6%" },
    { area: "", ap: "", cat: "", cp: "", param: "PULIZIA SOSTEGNI E MAN. CORRENTI", pp: "4%" },
    { area: "", ap: "", cat: "STATO DEL MEZZO", cp: "4%", param: "STATO ESTERNO DEL MEZZO", pp: "38%" },
    { area: "", ap: "", cat: "", cp: "", param: "FUMO DI SCARICO", pp: "38%" },
    { area: "", ap: "", cat: "", cp: "", param: "RUMOROSITÀ ESTERNA DEL VEICOLO", pp: "25%" },
    { area: "", ap: "", cat: "SICUREZZA", cp: "9%", param: "VIDEOCAMERE SORVEGLIANZA", pp: "50%" },
    { area: "", ap: "", cat: "", cp: "", param: "UTILIZZO CELLULARI", pp: "19%" },
    { area: "", ap: "", cat: "", cp: "", param: "UTILIZZO AURICOLARI", pp: "6%" },
    { area: "", ap: "", cat: "", cp: "", param: "DISTRAZIONE DALLA GUIDA", pp: "19%" },
    { area: "", ap: "", cat: "", cp: "", param: "ESPOSIZIONE GIORNALI/RIVISTE", pp: "6%" },
    { area: "", ap: "", cat: "PERSONALE", cp: "5%", param: "ACCOGLIENZA", pp: "11%" },
    { area: "", ap: "", cat: "", cp: "", param: "ASPETTO", pp: "6%" },
    { area: "", ap: "", cat: "", cp: "", param: "CARTELLINO RICONOSCIMENTO", pp: "6%" },
    { area: "", ap: "", cat: "", cp: "", param: "DIVISA", pp: "6%" },
    { area: "", ap: "", cat: "", cp: "", param: "ATTEGGIAMENTO", pp: "22%" },
    { area: "", ap: "", cat: "", cp: "", param: "DISPONIBILITÀ", pp: "11%" },
    { area: "", ap: "", cat: "", cp: "", param: "EROGAZIONE SERVIZIO", pp: "22%" },
    { area: "", ap: "", cat: "", cp: "", param: "GUIDA", pp: "11%" },
    { area: "", ap: "", cat: "", cp: "", param: "APERTURA PORTE", pp: "6%" },
    { area: "", ap: "", cat: "ACCESSIBILITÀ", cp: "5%", param: "AREE ATTRACCO CARROZZELLE", pp: "50%" },
    { area: "", ap: "", cat: "", cp: "", param: "POSTI RISERVATI DISABILI/UTENZA DEBOLE", pp: "50%" },
    { area: "", ap: "", cat: "COMUNICAZIONE", cp: "3%", param: "SCRITTE PER PROMOZIONE", pp: "100%" },
    // ── FERMATE 25% ──
    { area: "FERMATE", ap: "25%", cat: "TIPO FERMATA", cp: "4%", param: "PANNELLI PENSILINA", pp: "100%" },
    { area: "", ap: "", cat: "VISIBILITÀ", cp: "12%", param: "VISIBILITÀ", pp: "50%" },
    { area: "", ap: "", cat: "", cp: "", param: "RICONOSCIBILITÀ", pp: "50%" },
    { area: "", ap: "", cat: "STATO", cp: "12%", param: "USURA STRUTTURA", pp: "40%" },
    { area: "", ap: "", cat: "", cp: "", param: "SUPPORTI PER ATTESA (SE PENSILINA)", pp: "60%" },
    { area: "", ap: "", cat: "PULIZIA", cp: "10%", param: "PULIZIA PAVIMENTAZIONE (SE PENSILINA)", pp: "13%" },
    { area: "", ap: "", cat: "", cp: "", param: "PULIZIA SEDILI (SE PENSILINA)", pp: "13%" },
    { area: "", ap: "", cat: "", cp: "", param: "ADESIVI/SCRITTE SU STRUTTURA", pp: "13%" },
    { area: "", ap: "", cat: "", cp: "", param: "VANDALISMI SU AREE INFORMATIVE", pp: "63%" },
    { area: "", ap: "", cat: "INFORMAZIONI", cp: "36%", param: "PMV", pp: "43%" },
    { area: "", ap: "", cat: "", cp: "", param: "TABELLE ORARI", pp: "29%" },
    { area: "", ap: "", cat: "", cp: "", param: "LISTA PUNTI VENDITA", pp: "7%" },
    { area: "", ap: "", cat: "", cp: "", param: "NUMERO TELEFONO AZIENDA", pp: "7%" },
    { area: "", ap: "", cat: "", cp: "", param: "SITO WEB/APP", pp: "7%" },
    { area: "", ap: "", cat: "", cp: "", param: "INFO REPERIMENTO TITOLI", pp: "7%" },
    { area: "", ap: "", cat: "COMUNICAZIONE", cp: "5%", param: "INFO BIGLIETTI/ABBONAMENTI DISPONIBILI", pp: "50%" },
    { area: "", ap: "", cat: "", cp: "", param: "CAMPAGNA COMUNICAZIONE AZIENDALE", pp: "25%" },
    { area: "", ap: "", cat: "", cp: "", param: "CAMPAGNA COMUNICAZIONE DI TERZI", pp: "25%" },
    { area: "", ap: "", cat: "SICUREZZA", cp: "12%", param: "SEGNALETICA ORIZZONTALE", pp: "20%" },
    { area: "", ap: "", cat: "", cp: "", param: "SICUREZZA FERMATA RISPETTO A STRADA", pp: "80%" },
    { area: "", ap: "", cat: "ACCESSIBILITÀ", cp: "10%", param: "LEGGIBILITÀ ORARI", pp: "38%" },
    { area: "", ap: "", cat: "", cp: "", param: "AGEVOLAZIONI SOSTA DISABILI", pp: "31%" },
    { area: "", ap: "", cat: "", cp: "", param: "AGEVOLAZIONI PER NON VEDENTI", pp: "31%" },
    // ── PUNTI VENDITA 15% ──
    { area: "PUNTI VENDITA", ap: "15%", cat: "ATTESA", cp: "12%", param: "GESTIONE CODA", pp: "20%" },
    { area: "", ap: "", cat: "", cp: "", param: "TEMPO DI ATTESA", pp: "80%" },
    { area: "", ap: "", cat: "VISIBILITÀ", cp: "24%", param: "VISIBILITÀ", pp: "50%" },
    { area: "", ap: "", cat: "", cp: "", param: "RICONOSCIBILITÀ", pp: "50%" },
    { area: "", ap: "", cat: "SPAZIO", cp: "6%", param: "SPAZIO", pp: "100%" },
    { area: "", ap: "", cat: "ODORI", cp: "6%", param: "ODORI", pp: "100%" },
    { area: "", ap: "", cat: "ILLUMINAZIONE", cp: "6%", param: "ILLUMINAZIONE", pp: "100%" },
    { area: "", ap: "", cat: "PULIZIA", cp: "10%", param: "PULIZIA PAVIMENTAZIONE", pp: "25%" },
    { area: "", ap: "", cat: "", cp: "", param: "PULIZIA ATTREZZATURE VARIE", pp: "25%" },
    { area: "", ap: "", cat: "", cp: "", param: "PULIZIA UFFICIO", pp: "25%" },
    { area: "", ap: "", cat: "", cp: "", param: "PULIZIA SUPPORTI PER L'ATTESA", pp: "25%" },
    { area: "", ap: "", cat: "PERSONALE", cp: "36%", param: "ASPETTO", pp: "10%" },
    { area: "", ap: "", cat: "", cp: "", param: "ACCOGLIENZA", pp: "13%" },
    { area: "", ap: "", cat: "", cp: "", param: "ATTEGGIAMENTO", pp: "13%" },
    { area: "", ap: "", cat: "", cp: "", param: "DISPONIBILITÀ", pp: "20%" },
    { area: "", ap: "", cat: "", cp: "", param: "EROGAZIONE SERVIZIO", pp: "27%" },
    { area: "", ap: "", cat: "", cp: "", param: "PROATTIVITÀ", pp: "17%" },
    // ── CALL CENTER 5% ──
    { area: "CALL CENTER", ap: "5%", cat: "ATTESA", cp: "22%", param: "TEMPO DI ATTESA", pp: "40%" },
    { area: "", ap: "", cat: "", cp: "", param: "TENTATIVI FALLITI PRIMA DI PARLARE CON L'OPERATORE", pp: "60%" },
    { area: "", ap: "", cat: "PERSONALE", cp: "67%", param: "ACCOGLIENZA", pp: "13%" },
    { area: "", ap: "", cat: "", cp: "", param: "ATTEGGIAMENTO", pp: "13%" },
    { area: "", ap: "", cat: "", cp: "", param: "DISPONIBILITÀ", pp: "20%" },
    { area: "", ap: "", cat: "", cp: "", param: "SERVIZIO RESO", pp: "33%" },
    { area: "", ap: "", cat: "", cp: "", param: "PROATTIVITÀ", pp: "20%" },
    { area: "", ap: "", cat: "MESSAGGERIA", cp: "11%", param: "QUALITÀ AUDIO MESSAGGERIA", pp: "20%" },
    { area: "", ap: "", cat: "", cp: "", param: "CHIAREZZA ISTRUZIONI MESSAGGERIA", pp: "80%" },
    // ── SITO WEB 7,5% ──
    { area: "SITO WEB", ap: "7,5%", cat: "CONTENUTI", cp: "45%", param: "INFO LINEE E PERCORSI", pp: "24%" },
    { area: "", ap: "", cat: "", cp: "", param: "INFO TITOLI DI VIAGGIO", pp: "24%" },
    { area: "", ap: "", cat: "", cp: "", param: "MODALITÀ ACQUISTO TITOLI DI VIAGGIO", pp: "20%" },
    { area: "", ap: "", cat: "", cp: "", param: "CANALI CONTATTO AZIENDA", pp: "16%" },
    { area: "", ap: "", cat: "", cp: "", param: "CALCOLO PERCORSO", pp: "16%" },
    { area: "", ap: "", cat: "ESPERIENZA UTENTE", cp: "27%", param: "NAVIGABILITÀ", pp: "24%" },
    { area: "", ap: "", cat: "", cp: "", param: "CHIAREZZA INFO", pp: "32%" },
    { area: "", ap: "", cat: "", cp: "", param: "COMPLETEZZA INFO", pp: "32%" },
    { area: "", ap: "", cat: "", cp: "", param: "RESPONSIVENESS", pp: "12%" },
    { area: "", ap: "", cat: "FUNZIONI", cp: "9%", param: "ELENCO BIGLIETTERIE", pp: "20%" },
    { area: "", ap: "", cat: "", cp: "", param: "INFO IN TEMPO REALE", pp: "70%" },
    { area: "", ap: "", cat: "", cp: "", param: "INFO SU EVENTI TERRITORIO", pp: "10%" },
    { area: "", ap: "", cat: "SERVIZI", cp: "18%", param: "RIMANDO SOCIAL", pp: "16%" },
    { area: "", ap: "", cat: "", cp: "", param: "INDICAZIONE ALL'APP", pp: "42%" },
    { area: "", ap: "", cat: "", cp: "", param: "E-COMMERCE", pp: "42%" },
    // ── APP 7,5% ──
    { area: "APP", ap: "7,5%", cat: "CONTENUTI", cp: "33%", param: "INFO LINEE E PERCORSI", pp: "50%" },
    { area: "", ap: "", cat: "", cp: "", param: "CALCOLO PERCORSO", pp: "25%" },
    { area: "", ap: "", cat: "", cp: "", param: "INFO REAL TIME", pp: "25%" },
    { area: "", ap: "", cat: "ESPERIENZA UTENTE", cp: "33%", param: "CONTENUTI E CHIAREZZA", pp: "29%" },
    { area: "", ap: "", cat: "", cp: "", param: "ORDINE E CURA", pp: "14%" },
    { area: "", ap: "", cat: "", cp: "", param: "NAVIGABILITÀ", pp: "29%" },
    { area: "", ap: "", cat: "", cp: "", param: "RESPONSIVENESS", pp: "29%" },
    { area: "", ap: "", cat: "FUNZIONALITÀ", cp: "33%", param: "ACQUISTO TITOLI DI VIAGGIO", pp: "80%" },
    { area: "", ap: "", cat: "", cp: "", param: "ELENCO PUNTI VENDITA", pp: "20%" },
  ];

  const CA = 26, CaP = 11, CC = 36, CcP = 11, CP = 152, CpP = 13;
  const totalW = CA + CaP + CC + CcP + CP + CpP;
  const tableX = ML + (W - ML - MR - totalW) / 2;
  const ROW_H = 5.5;
  const HDR_H = 8;

  let y = 0;

  const drawTableHeader = () => {
    SF(doc, C.dark); doc.rect(tableX, y, totalW, HDR_H, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(6.5); ST(doc, C.lime);
    let cx = tableX;
    const lbl = (t, w) => { doc.text(t, cx + 2, y + 5.5); cx += w; };
    lbl("AREA", CA); lbl("PESO", CaP); lbl("CATEGORIA", CC); lbl("PESO", CcP);
    lbl("PARAMETRO / SOTTOCATEGORIA", CP); lbl("PESO", CpP);
    y += HDR_H;
  };

  stdFrame(doc, company, city, pn);
  doc.setFont("helvetica", "bold"); doc.setFontSize(18); ST(doc, C.dark);
  doc.text("Modello di pesatura e indicatori monitorati", W / 2, Y0 + 9, { align: "center" });
  SF(doc, C.lime); doc.rect(tableX, Y0 + 12, totalW, 2, "F");
  y = Y0 + 17;
  drawTableHeader();

  let lastArea = "", lastCat = "";

  D.forEach((row, ri) => {
    if (y + ROW_H > YMAX - 1) {
      doc.addPage(); pn++;
      stdFrame(doc, company, city, pn);
      y = Y0 + 2;
      drawTableHeader();
      lastArea = ""; lastCat = "";
    }

    const newArea = row.area !== "" && row.area !== lastArea;
    const newCat = row.cat !== "" && row.cat !== lastCat;
    if (newArea) { lastArea = row.area; }
    if (newCat) { lastCat = row.cat; }

    let cx = tableX;
    const shade = ri % 2;

    const areaBg = "#1A2808";
    SF(doc, areaBg); doc.rect(cx, y, CA, ROW_H, "F");
    if (newArea) {
      doc.setFont("helvetica", "bold"); doc.setFontSize(5.8); ST(doc, C.white);
      doc.text(row.area, cx + 2, y + 3.9, { maxWidth: CA - 3 });
    }
    cx += CA;

    SF(doc, areaBg); doc.rect(cx, y, CaP, ROW_H, "F");
    if (newArea) {
      doc.setFont("helvetica", "bold"); doc.setFontSize(6); ST(doc, C.lime);
      doc.text(row.ap, cx + CaP - 2, y + 3.9, { align: "right" });
    }
    cx += CaP;

    const catBg = "#243E0A";
    SF(doc, catBg); doc.rect(cx, y, CC, ROW_H, "F");
    if (newCat) {
      doc.setFont("helvetica", "bold"); doc.setFontSize(5.8); ST(doc, C.white);
      doc.text(row.cat, cx + 2, y + 3.9, { maxWidth: CC - 3 });
    }
    cx += CC;

    SF(doc, catBg); doc.rect(cx, y, CcP, ROW_H, "F");
    if (newCat) {
      doc.setFont("helvetica", "bold"); doc.setFontSize(6); ST(doc, C.lime);
      doc.text(row.cp, cx + CcP - 2, y + 3.9, { align: "right" });
    }
    cx += CcP;

    const paramBg = shade === 0 ? C.cream : C.white;
    SF(doc, paramBg); doc.rect(cx, y, CP, ROW_H, "F");
    doc.setFont("helvetica", "normal"); doc.setFontSize(5.8); ST(doc, C.text);
    doc.text(row.param, cx + 3, y + 3.9, { maxWidth: CP - 5 });
    cx += CP;

    SF(doc, paramBg); doc.rect(cx, y, CpP, ROW_H, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(6.5); ST(doc, C.mid);
    doc.text(row.pp, cx + CpP - 2, y + 3.9, { align: "right" });

    y += ROW_H;
  });

  return pn;
}

// ═══════════════════════════════════════════════════════════════════════════════
//  DIVIDER SEZIONE
// ═══════════════════════════════════════════════════════════════════════════════
function addSectionDivider(doc, _company, _city, title, _subtitle, pn, logoB64 = null, gametLogoB64 = null, companyLogoB64 = null, dividerBgB64 = null) {
  if (!dividerBgB64 || !addPdfImage(doc, dividerBgB64, 0, 0, W, H)) {
    SF(doc, C.divBg); doc.rect(0, 0, W, H, "F");
  }
  SF(doc, C.lime); doc.rect(0, 0, W, 2, "F");
  SF(doc, C.lime); doc.rect(0, H - 2, W, 2, "F");

  // ── GAMET logo — top-left, larger ──
  if (gametLogoB64) {
    const gametW = 60, gametH = 15;
    const gametX = ML - 5, gametY = 6;
    addPdfImage(doc, gametLogoB64, gametX, gametY, gametW, gametH);
  }

  // ── Exis logo — top-right (unchanged size) ──
  const logoW = 44, logoH = 44 / (2977 / 1080);
  const logoX = W - MR - logoW, logoY = 7;
  if (logoB64) {
    addPdfImage(doc, logoB64, logoX, logoY, logoW, logoH);
  } else {
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); ST(doc, C.lime);
    doc.text("Exis Mobility", logoX + 2, logoY + logoH / 2 + 2);
  }

  // ── Company logo — bottom-right, white, roughly same size as Exis ──
  if (companyLogoB64) {
    const clW = 44, clH = 44 / (2977 / 1080);
    const clX = W - MR - clW, clY = H - 4 - clH - 6;
    addPdfImage(doc, companyLogoB64, clX, clY, clW, clH);
  }

  doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); ST(doc, C.muted);
  doc.text(String(pn), W - MR, H - 4, { align: "right" });

  const titleLines = title.split("\n");
  const maxLen = Math.max(...titleLines.map(l => l.length));
  const fontSize = maxLen > 14 ? 52 : maxLen > 10 ? 60 : 72;
  const lineSpacingMM = fontSize * 0.353 * 1.35;
  const titleBlockH = (titleLines.length - 1) * lineSpacingMM + fontSize * 0.4;
  doc.setFont("helvetica", "bold"); doc.setFontSize(fontSize); ST(doc, C.white);
  let ty = H / 2 - titleBlockH / 2 + 8;
  for (const tl of titleLines) {
    doc.text(tl, ML, ty);
    ty += lineSpacingMM;
  }

  const barY = H / 2 - titleBlockH / 2 + 8 + titleBlockH + 6;
  SF(doc, C.lime); doc.rect(ML, barY, 200, 4, "F");
}

// ═══════════════════════════════════════════════════════════════════════════════
//  TABELLA VALUTAZIONE COMPLESSIVA
// ═══════════════════════════════════════════════════════════════════════════════
function addScoreTable(doc, company, city, pn) {
  const TABLE_W = W - ML - MR - 48;
  const TABLE_X = ML + (W - ML - MR - TABLE_W) / 2;
  const C1 = TABLE_X, C2 = TABLE_X + 58, C3 = TABLE_X + TABLE_W - 32, CW3 = 32;
  const ROW_H = 8, HDR_H = 9;
  let y = 0;

  const drawHdr = () => {
    SF(doc, C.dark); doc.rect(TABLE_X, y, TABLE_W, HDR_H, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); ST(doc, C.lime);
    doc.text("CATEGORIA", C1 + 2, y + 6.2);
    doc.text("SOTTOCATEGORIA", C2 + 2, y + 6.2);
    doc.text("PUNTEGGIO", C3 + CW3 / 2, y + 6.2, { align: "center" });
    y += HDR_H;
  };

  const ensureSpace = (need) => {
    if (y + need > YMAX) {
      doc.addPage(); pn++;
      stdFrame(doc, company, city, pn);
      y = Y0 + 2;
      drawHdr();
    }
  };

  stdFrame(doc, company, city, pn);
  doc.setFont("helvetica", "bold"); doc.setFontSize(22); ST(doc, C.dark);
  doc.text("Valutazione complessiva", W / 2, Y0 + 10, { align: "center" });
  SF(doc, C.lime); doc.rect(TABLE_X, Y0 + 13, TABLE_W, 2.5, "F");
  y = Y0 + 20; drawHdr();

  const cd = getCityData(company, city).data;
  const lineeCat = getLineeCategoryForCity(cd);
  const SECTIONS = [
    ...(lineeCat ? [{ key: lineeCat.key, label: lineeCat.label }] : []),
    { key: "fermate", label: "FERMATE" },
    { key: "punti_vendita", label: "PUNTI VENDITA" },
    { key: "call_center", label: "CALL CENTER" },
    { key: "sito_web", label: "SITO WEB" },
    { key: "app", label: "APP" },
  ];

  let rowIdx = 0;

  SECTIONS.forEach(sec => {
    const catData = cd[sec.key];
    if (!catData) return;
    const subs = catData.sub
      ? Object.entries(catData.sub).map(([k, v]) => [k, v?.punteggio_globale ?? null])
      : [];

    subs.forEach(([subKey, score], i) => {
      ensureSpace(ROW_H + 1);
      const bg = rowIdx % 2 === 0 ? C.white : C.cream;
      SF(doc, bg); doc.rect(TABLE_X, y, TABLE_W, ROW_H, "F");
      SF(doc, C.dark); doc.rect(C1, y, 3, ROW_H, "F");

      if (i === 0) {
        doc.setFont("helvetica", "bold"); doc.setFontSize(8); ST(doc, C.dark);
        doc.text(sec.label, C1 + 5, y + ROW_H / 2 + 2.8, { maxWidth: C2 - C1 - 8 });
      }

      doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); ST(doc, C.text);
      doc.text(capFirst(subKey), C2 + 2, y + ROW_H / 2 + 2.8, { maxWidth: C3 - C2 - 4 });

      if (score !== null) {
        const sc = scoreGradText(score);
        doc.setFont("helvetica", "bold"); doc.setFontSize(9); ST(doc, sc);
        doc.text(fmt(score, 2), C3 + CW3 / 2, y + ROW_H / 2 + 2.8, { align: "center" });
      }

      SD(doc, C.border); doc.setLineWidth(0.1);
      doc.line(C2, y + ROW_H, TABLE_X + TABLE_W, y + ROW_H);

      y += ROW_H; rowIdx++;
    });

    // Media pesata
    ensureSpace(ROW_H + 4);
    SF(doc, C.dark); doc.rect(TABLE_X, y, TABLE_W, ROW_H, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); ST(doc, C.lime);
    doc.text("MEDIA PESATA", C2 + 2, y + ROW_H / 2 + 2.8);
    const globalScore = catData.punteggio_globale;
    if (globalScore !== null && globalScore !== undefined) {
      const sc = scoreGradText(globalScore);
      doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); ST(doc, sc);
      doc.text(fmt(globalScore, 2), C3 + CW3 / 2, y + ROW_H / 2 + 2.8, { align: "center" });
    }
    y += ROW_H + 4; rowIdx++;
  });

  // Indice sintetico
  ensureSpace(ROW_H + 20);
  y += 3;
  const gs = getIndiceSintetico(company, city);
  SF(doc, C.lime); doc.rect(TABLE_X, y, TABLE_W, ROW_H + 2, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); ST(doc, C.dark);
  doc.text("INDICE SINTETICO", C1 + 2, y + ROW_H / 2 + 3.5);
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); ST(doc, C.dark);
  doc.text(fmt(gs, 2), C3 + CW3 / 2, y + ROW_H / 2 + 3.5, { align: "center" });

  y += ROW_H + 12;
  if (y + 12 < YMAX) {
    doc.setFont("helvetica", "normal"); doc.setFontSize(7); ST(doc, C.muted);
    doc.text(
      `La valutazione degli ambiti di indagine prevede l'attribuzione di un voto da 1 a 10 applicando il modello di pesatura.`,
      TABLE_X, y, { maxWidth: TABLE_W }
    );
  }
  return pn;
}

// ═══════════════════════════════════════════════════════════════════════════════
//  PAGINA VALUTAZIONE SINTETICA (*GRAFICO_MACROCATEGORIE*)
// ═══════════════════════════════════════════════════════════════════════════════
function addOverview(doc, company, city, pn) {
  stdFrame(doc, company, city, pn);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); ST(doc, C.muted);
  doc.text(
    `Punteggi Mystery Client ${company.anno}-${company.anno + 1}`,
    W / 2, Y0 + 4, { align: "center" }
  );
  doc.setFont("helvetica", "bold"); doc.setFontSize(26); ST(doc, C.dark);
  doc.text("Valutazione sintetica", W / 2, Y0 + 12, { align: "center" });

  const cd = getCityData(company, city).data;
  const lineeCat = getLineeCategoryForCity(cd);
  const bars = [
    ...(lineeCat ? [{ label: lineeCat.barLabel, score: getLineeScore(cd) }] : []),
    { label: "Fermate", score: cd.fermate?.punteggio_globale },
    { label: "Punti Vendita", score: cd.punti_vendita?.punteggio_globale },
    { label: "Call Center", score: cd.call_center?.punteggio_globale },
    { label: "Sito", score: cd.sito_web?.punteggio_globale },
    { label: "App", score: cd.app?.punteggio_globale },
    { label: "INDICE\nSINTETICO", score: getIndiceSintetico(company, city), highlight: true },
  ].filter(b => b.score !== null && b.score !== undefined);

  const chartX = ML + 14;
  const chartY = Y0 + 16;
  const chartW = W - ML - MR - 14;
  const chartH = H - chartY - FOOT - 18;
  const n = bars.length;
  const barW = Math.min(24, (chartW / n) - 6);
  const gap = (chartW - barW * n) / (n + 1);

  doc.setLineWidth(0.2);
  for (let v = 0; v <= 10; v++) {
    const ly = chartY + chartH - (v / 10) * chartH;
    SD(doc, v % 2 === 0 ? C.grid : C.border);
    doc.line(chartX, ly, chartX + chartW, ly);
    doc.setFont("helvetica", "normal"); doc.setFontSize(7); ST(doc, C.muted);
    doc.text(String(v), chartX - 4, ly + 1.5, { align: "right" });
  }

  bars.forEach((b, i) => {
    const bx = chartX + gap + i * (barW + gap);
    const bh = (b.score / 10) * chartH;
    const by = chartY + chartH - bh;
    const color = scoreGrad(b.score);
    SF(doc, color); doc.roundedRect(bx, by, barW, bh, 2, 2, "F");
    if (bh > 4) { SF(doc, color); doc.rect(bx, by + 2, barW, bh - 2, "F"); }
    if (b.highlight) {
      SD(doc, C.dark); doc.setLineWidth(0.6);
      doc.roundedRect(bx, by, barW, bh, 2, 2, "S");
    }
    doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); ST(doc, C.dark);
    doc.text(fmt(b.score, 2), bx + barW / 2, by - 3, { align: "center" });
    const labelLines = b.label.split("\n");
    doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); ST(doc, C.text);
    labelLines.forEach((line, li) => {
      doc.text(line, bx + barW / 2, chartY + chartH + 7 + li * 6, { align: "center" });
    });
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
//  HEADER SOTTOCATEGORIA
// ═══════════════════════════════════════════════════════════════════════════════
function subcatPageHeader(lay, catLabel, subKey, score) {
  const d = lay.doc;
  const showSub = subKey && subKey.toLowerCase().replace(/_/g, " ") !== catLabel.toLowerCase();

  d.setFont("helvetica", "bold"); d.setFontSize(26); ST(d, C.dark);
  d.text(catLabel.toUpperCase(), ML, lay.y + 16, { maxWidth: W - ML - MR - 55 });

  if (score !== null && score !== undefined) {
    const sc = scoreGradText(score);
    d.setFont("helvetica", "bold"); d.setFontSize(15); ST(d, sc);
    d.text(`${fmt(score, 2)} / 10`, W - MR - 4, lay.y + 16, { align: "right" });
  }

  if (showSub) {
    d.setFont("helvetica", "bold"); d.setFontSize(12); ST(d, C.mid);
    d.text(`› ${capFirst(subKey)}`, ML + 1, lay.y + 27, { maxWidth: W - ML - MR - 55 });
  }

  const lineY = lay.y + (showSub ? 31 : 20);
  SF(d, C.lime); d.rect(ML, lineY, W - ML - MR, 2.5, "F");
  lay.y = lineY + 7;
}

// ═══════════════════════════════════════════════════════════════════════════════
//  RENDER CATEGORIA
// ═══════════════════════════════════════════════════════════════════════════════
function renderCat(lay, catLabel, catData) {
  if (!catData) return;
  lay.cat = catLabel;

  const subs = catData.sub ? Object.entries(catData.sub) : [];
  const grafici = catData.grafici ?? {};

  for (const [subKey, subVal] of subs) {
    const group = grafici[subKey];
    const charts = group && typeof group === "object"
      ? Object.entries(group).filter(([, v]) => typeof v === "object" && v !== null)
      : [];

    lay.newPage();
    subcatPageHeader(lay, catLabel, subKey, subVal?.punteggio_globale ?? null);

    if (charts.length > 0) {
      for (const [chartName, chartData] of charts) {
        lay.render(chartName, chartData);
      }
    } else if (subVal?.breakdown && Object.keys(subVal.breakdown).length > 0) {
      lay.render(subKey, subVal.breakdown);
    } else {
      lay.doc.setFont("helvetica", "normal"); lay.doc.setFontSize(8);
      ST(lay.doc, C.muted);
      lay.doc.text("Dati non disponibili per questa sottocategoria.", ML, lay.y + 6);
      lay.y += 14;
    }
  }

  if (!subs.length && Object.keys(grafici).length > 0) {
    lay.newPage();
    subcatPageHeader(lay, catLabel, catLabel.toLowerCase(), catData.punteggio_globale ?? null);
    for (const [chartName, chartData] of Object.entries(grafici)) {
      lay.render(chartName, chartData);
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
//  ENTRY POINT
// ═══════════════════════════════════════════════════════════════════════════════
export async function generateReport(company, selectedCity) {
  const citiesToPrint = getCitiesToPrint(company, selectedCity);
  if (citiesToPrint.length === 0) return;

  const NO_GAMET = ["ATB", "STPS"];
  const showGamet = !NO_GAMET.includes((company.azienda || "").toUpperCase());
  const [logoB64, rawGametLogoB64, slideImages] = await Promise.all([
    loadImageB64("/exis.png"),
    showGamet ? loadImageB64("/gamet.png") : Promise.resolve(null),
    loadReportSlideImages(company, selectedCity),
  ]);
  const gametLogoB64 = rawGametLogoB64;

  // Load company logo and convert to white for dark backgrounds
  let companyLogoB64 = null;
  try {
    const blobUrl = await getCompanyLogoWithAuth(company.azienda, company.anno);
    companyLogoB64 = await loadBlobUrlAsDataUrl(blobUrl);
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  } catch {
    // Company logo is optional on the PDF cover.
  }

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  let pn = 1;

  const coverCityText = citiesToPrint.join(" - ").toUpperCase();
  const primaryCity = citiesToPrint.length === 1 ? citiesToPrint[0] : null;

  // ── Pagina 1: Cover (Una per tutto il documento)
  addCover(
    doc, company, coverCityText, logoB64, gametLogoB64, companyLogoB64,
    getCoverBackground(slideImages, primaryCity)
  );

  // ── Pagina 2: Premessa
  doc.addPage(); pn++;
  addPremessa(doc, company, coverCityText, pn);

  // ── Pagine 3+: Modello di pesatura + Indicatori monitorati
  doc.addPage(); pn++;
  pn = addPesaturaIndicatori(doc, company, coverCityText, pn);

  for (let i = 0; i < citiesToPrint.length; i++) {
    const city = citiesToPrint[i];
    
    // ── Divider: EXECUTIVE SUMMARY
    doc.addPage(); pn++;
    const divTitle = citiesToPrint.length > 1 ? `EXECUTIVE\nSUMMARY\n${city.toUpperCase()}` : "EXECUTIVE\nSUMMARY";
    addSectionDivider(doc, company, city,
      divTitle,
      "Valutazione complessiva · Valutazione sintetica",
      pn, logoB64, gametLogoB64, companyLogoB64,
      getDividerBackground(slideImages, city, "EXECUTIVE")
    );

    // ── Pagine: Tabella punteggi
    doc.addPage(); pn++;
    pn = addScoreTable(doc, company, city, pn);

    // ── Pagina: Grafico macro
    doc.addPage(); pn++;
    addOverview(doc, company, city, pn);

    // ── Categorie con chart pages
    const lay = new Lay(doc, company, city);
    lay.pn = pn;

    const cd = getCityData(company, city).data;

    const SECTIONS = [
      { key: "punti_vendita", label: "Punti Vendita", divider: "PUNTI VENDITA" },
      { key: "fermate", label: "Fermate", divider: "FERMATE" },
      { key: "linee_urbane", label: "Linee Urbane", divider: "LINEE" },
      { key: "call_center", label: "Call Center", divider: "CALL CENTER" },
      { key: "sito_web", label: "Sito Web", divider: "WEB E APP" },
      { key: "app", label: "App", divider: "" },
    ];

    let lastDivider = "";
    for (const sec of SECTIONS) {
      if (!cd[sec.key]) continue;

      if (sec.divider && sec.divider !== lastDivider) {
        doc.addPage(); lay.pn++;
        addSectionDivider(
          doc, company, city,
          sec.divider,
          "ELEMENTI CARATTERIZZANTI SERVIZIO AUTOMOBILISTICO",
          lay.pn, logoB64, gametLogoB64, companyLogoB64,
          getDividerBackground(slideImages, city, sec.divider)
        );
        lastDivider = sec.divider;
      }

      renderCat(lay, sec.label, cd[sec.key]);
    }
    
    pn = lay.pn;
  }

  doc.save(getReportFileName(company, selectedCity, "pdf"));
}
